import { search as jmespathSearch } from '@jmespath-community/jmespath';
import { FetchApi } from '@backstage/core-plugin-api';

// Helper function to extract value from formData using JMESPath
export const extractValueFromFormData = (key: string, formData: Record<string, any>): { value: any; exists: boolean } => {
    try {
        // Convert the key format to JMESPath format
        // Handle array indexing like "names[0]" -> "names[0]"
        // Handle nested properties like "user.name" -> "user.name"
        // Handle simple keys like "x" -> "x"
        const jmespathQuery = key;
        
        const result = jmespathSearch(formData, jmespathQuery);
        
        // JMESPath returns null for non-existent paths, so we check for that
        return { 
            value: result, 
            exists: result !== null 
        };
    } catch (error) {
        // If JMESPath fails to parse or execute the query, return not found
        return { value: null, exists: false };
    }
};

// Generic template substitution function
export const substituteTemplate = <T>(
    template: string | string[],
    formData: Record<string, any>,
    replacementFn: (value: any, key: string) => string
): { 
    result: T; 
    isValid: boolean; 
    missingProperties: string[] 
} => {
    const missingProperties: string[] = [];
    
    if (Array.isArray(template)) {
        // Handle array of templates (like scopes)
        const result = template.map(item => {
            return item.replace(/\{([^}]+)\}/g, (match, key) => {
                const { value, exists } = extractValueFromFormData(key, formData);
                
                if (exists) {
                    return replacementFn(value, key);
                }
                
                missingProperties.push(key);
                return match; // Return original if not found
            });
        }) as T;
        
        return {
            result,
            isValid: missingProperties.length === 0,
            missingProperties
        };
    } else {
        // Handle single template (like URL)
        const result = template.replace(/\{([^}]+)\}/g, (match, key) => {
            const { value, exists } = extractValueFromFormData(key, formData);
            
            if (exists) {
                return replacementFn(value, key);
            }
            
            missingProperties.push(key);
            return match; // Return original if not found
        }) as T;
        
        return {
            result,
            isValid: missingProperties.length === 0,
            missingProperties
        };
    }
};

// Specific helper for URL template substitution
export const substituteUrlTemplate = (template: string, formData: Record<string, any>): { 
    finalUrl: string; 
    isValid: boolean; 
    missingProperties: string[] 
} => {
    const { result, isValid, missingProperties } = substituteTemplate<string>(
        template, 
        formData, 
        (value) => encodeURIComponent(String(value))
    );
    
    return {
        finalUrl: result,
        isValid,
        missingProperties
    };
};

// Placeholder syntax for JMESPath templating: {{key}} (double curly braces),
// deliberately distinct from substituteTemplate's {key} syntax. JMESPath's own
// multi-select-hash syntax uses single braces ({a: b, ...}), and the character
// after any "{" in valid JMESPath must start an identifier/string — never
// another "{" — so a literal "{{" can never occur in real JMESPath. This
// regex is intentionally separate from substituteTemplate's and must not be merged with it.
const JMES_PATH_PLACEHOLDER_REGEX = /\{\{\s*([^{}]+?)\s*\}\}/g;

// Escapes a value for safe use inside a single-quoted JMESPath raw string
// literal. Per the JMESPath grammar, only \ and ' need escaping inside a raw
// string; the template author supplies their own quotes around the
// placeholder (or omits them for numeric/boolean comparisons).
const escapeJmesPathRawString = (raw: string): string =>
    raw.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

// Specific helper for JMESPath template substitution
export const substituteJmesPathValueTemplate = (
    template: string,
    formData: Record<string, any>
): {
    result: string;
    isValid: boolean;
    missingProperties: string[]
} => {
    const missingProperties: string[] = [];

    const result = template.replace(JMES_PATH_PLACEHOLDER_REGEX, (match, key) => {
        const { value, exists } = extractValueFromFormData(key.trim(), formData);

        if (exists) {
            return escapeJmesPathRawString(String(value));
        }

        missingProperties.push(key.trim());
        return match; // leave "{{key}}" untouched, same contract as substituteTemplate
    });

    return {
        result,
        isValid: missingProperties.length === 0,
        missingProperties
    };
};

// Specific helper for scope template substitution
export const substituteScopes = (originalScopes: string[], formData: Record<string, any>): { 
    scopes: string[]; 
    isValid: boolean; 
    missingProperties: string[] 
} => {
    const { result, isValid, missingProperties } = substituteTemplate<string[]>(
        originalScopes, 
        formData, 
        (value) => String(value)
    );
    
    return {
        scopes: result,
        isValid,
        missingProperties
    };
};

export interface UserAccessResult {
    canApprove: boolean;
    error?: string;
}

export const fetchUserAdoAccess = async (
    project: string,
    organization: string,
    pipelineId: string,
    token: string,
    fetchApi: FetchApi,
    environmentName?: string,
): Promise<UserAccessResult> => {
    try {
        const context: AzureDevOpsContext = { organization, project, token, fetchApi };

        const pipelineValidation = await validatePipelineAccess(pipelineId, context);
        if (!pipelineValidation.isValid) {
            return { canApprove: false, error: pipelineValidation.error };
        }

        const queueBuildValidation = await checkCanUserQueueBuild(pipelineId, context);
        if (!queueBuildValidation.isValid) {
            return { canApprove: false, error: queueBuildValidation.error };
        }

        if(environmentName) {
            const environmentLookup = await getEnvironmentId(environmentName, context);
            if (environmentLookup.error || !environmentLookup.environmentId) {
                return { canApprove: false, error: environmentLookup.error };
            }

            const permissionValidation = await validateEnvironmentPermissions(
                environmentLookup.environmentId,
                pipelineId,
                context
            );
            if (!permissionValidation.isValid) {
                return { canApprove: false, error: permissionValidation.error };
            }

            const canUserApprove = await checkCanUserApprove(environmentLookup.environmentId, context);

            return canUserApprove
                ? { canApprove: true }
                : { canApprove: false, error: 'You do not have approval permissions for this environment' };
        }

        return { canApprove: true };
    } catch (error) {
        return {
            canApprove: false,
            error: `Error fetching user access to run pipeline: ${(error as Error).message}`
        };
    }
};

interface ValidationResult {
    isValid: boolean;
    error?: string;
}

interface AzureDevOpsContext {
    organization: string;
    project: string;
    token: string;
    fetchApi: FetchApi;
}

interface AzureDevOpsUser {
    authenticatedUser: {
        subjectDescriptor: string;
    };
}

interface AzureDevOpsApprover {
    id: string;
    descriptor?: string;
}

interface AzureDevOpsApprovalCheck {
    isDisabled: boolean;
    type?: {
        name: string;
    };
    settings?: {
        approvers?: AzureDevOpsApprover[];
    };
}

interface AzureDevOpsEnvironmentConfig {
    value?: AzureDevOpsApprovalCheck[];
}

interface AzureDevOpsDescriptor {
    value?: string;
}

interface AzureDevOpsMembershipCheck {
    message?: string;
}

interface AzureDevOpsFetchParams {
    url: string;
    token: string;
    fetchApi: FetchApi;
}

const azureDevOpsFetch = async <T = any>({url, token, fetchApi}: AzureDevOpsFetchParams): Promise<T> => {
    if (!token || !url || !fetchApi) {
        throw new Error(`Missing required parameters: ${!token ? 'token' : ''} ${!url ? 'url' : ''} ${!fetchApi ? 'fetchApi' : ''}`.trim());
    }
    
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };
    
    const response = await fetchApi.fetch(url, { headers });
    return response.json();
};

const validatePipelineAccess = async (
    pipelineId: string,
    context: AzureDevOpsContext
): Promise<ValidationResult> => {
    const pipelineResult = await azureDevOpsFetch({
        url: `https://dev.azure.com/${context.organization}/${context.project}/_apis/pipelines/${pipelineId}?api-version=7.1`,
        token: context.token,
        fetchApi: context.fetchApi
    });

    return pipelineResult.message
        ? { isValid: false, error: pipelineResult.message }
        : { isValid: true };
};

const checkCanUserQueueBuild = async (
    pipelineId: string,
    context: AzureDevOpsContext
): Promise<ValidationResult> => {
    // https://github.com/microsoft/terraform-provider-azuredevops/issues/1040#issuecomment-2105596397
    const queueBuildPermissionId = '128';
    // https://learn.microsoft.com/en-us/azure/devops/organizations/security/namespace-reference?view=azure-devops#object-level-namespaces-and-permissions
    const buildsecurityNamespaceId = '33344d9c-fc72-4d6f-aba5-fa317101a7e9';

    const buildDefinition = await azureDevOpsFetch({
        url: `https://dev.azure.com/${context.organization}/${context.project}/_apis/build/definitions/${pipelineId}?api-version=7.1`,
        token: context.token,
        fetchApi: context.fetchApi
    });

    if(buildDefinition.message) {
        return { isValid: false, error: buildDefinition.message };
    }

    const queueBuildResult = await azureDevOpsFetch({
        url: `https://dev.azure.com/${context.organization}/_apis/permissions/${buildsecurityNamespaceId}/${queueBuildPermissionId}?tokens=${buildDefinition.project.id}/${pipelineId}&api-version=7.1`,
        token: context.token,
        fetchApi: context.fetchApi
    });

    if(queueBuildResult.message) {
        return { isValid: false, error: queueBuildResult.message };
    }

    return queueBuildResult.value[0] === true
        ? { isValid: true }
        : { isValid: false, error: 'User does not have permissions to queue the pipeline run' };
};

const getEnvironmentId = async (
    environmentName: string,
    context: AzureDevOpsContext
): Promise<{ environmentId?: number; error?: string }> => {
    const env = await azureDevOpsFetch({
        url: `https://dev.azure.com/${context.organization}/${context.project}/_apis/pipelines/environments?name=${environmentName}&api-version=7.2-preview.1`,
        token: context.token,
        fetchApi: context.fetchApi
    });

    if (!env.value || env.value.length !== 1) {
        return {
            error: `Environment '${environmentName}' not found or multiple environments with the same name exist`
        };
    }

    return { environmentId: env.value[0].id };
};

const validateEnvironmentPermissions = async (
    environmentId: number,
    pipelineId: string,
    context: AzureDevOpsContext
): Promise<ValidationResult> => {
    const environmentPermissions = await azureDevOpsFetch<{ message?: string; pipelines: Array<{ id: number; authorized: boolean }>; allPipelines?: { authorized: boolean } }>({
        url: `https://dev.azure.com/${context.organization}/${context.project}/_apis/pipelines/pipelinepermissions/environment/${environmentId}?api-version=7.2-preview.1`,
        token: context.token,
        fetchApi: context.fetchApi,
    });

    if (environmentPermissions.message) {
        return { isValid: false, error: environmentPermissions.message };
    }

    if(environmentPermissions.allPipelines && environmentPermissions.allPipelines.authorized === true) {
        return { isValid: true };
    }

    const hasAccess = environmentPermissions.pipelines.some((p) => p.id.toString() === pipelineId && p.authorized === true);

    return hasAccess
        ? { isValid: true }
        : { isValid: false, error: 'User does not have access to the environment or wrong environment ID' };
};

const isUserDirectApprover = (approver: AzureDevOpsApprover, userDescriptor: string): boolean => {
    return !!approver.descriptor && approver.descriptor === userDescriptor;
};

const isUserGroupMemberApprover = async (
    approver: AzureDevOpsApprover,
    userDescriptor: string,
    context: AzureDevOpsContext
): Promise<boolean> => {
    const descriptor = await azureDevOpsFetch<AzureDevOpsDescriptor>({
        url: `https://vssps.dev.azure.com/${context.organization}/_apis/graph/descriptors/${approver.id}?api-version=7.2-preview.1`,
        token: context.token,
        fetchApi: context.fetchApi,
    });

    if (descriptor.value?.startsWith('svc_')) {
        return false;
    }

    const membershipCheck = await azureDevOpsFetch<AzureDevOpsMembershipCheck>({
        url: `https://vssps.dev.azure.com/${context.organization}/_apis/graph/memberships/${userDescriptor}/${descriptor.value ?? ''}?api-version=7.2-preview.1`,
        token: context.token,
        fetchApi: context.fetchApi,
    });

    // Message present means there was an error - user is not a member
    return !membershipCheck.message;
};

const checkCanUserApprove = async (
    environmentId: number,
    context: AzureDevOpsContext
): Promise<boolean> => {
    const user = await azureDevOpsFetch<AzureDevOpsUser>({
        url: `https://dev.azure.com/${context.organization}/_apis/connectiondata`,
        token: context.token,
        fetchApi: context.fetchApi,
    });

    const environmentConfig = await azureDevOpsFetch<AzureDevOpsEnvironmentConfig>({
        url: `https://dev.azure.com/${context.organization}/${context.project}/_apis/pipelines/checks/configurations?resourceType=environment&resourceId=${environmentId}&$expand=settings&api-version=7.1-preview.1`,
        token: context.token,
        fetchApi: context.fetchApi,
    });

    const approvalChecks = (environmentConfig.value ?? []).filter(
        (item) => !item.isDisabled && item.type?.name === 'Approval'
    );

    // No approval checks means no approval required
    if (approvalChecks.length === 0) {
        return true;
    }

    const userDescriptor = user.authenticatedUser.subjectDescriptor;

    for (const check of approvalChecks) {
        const approvers = check.settings?.approvers;
        if (!Array.isArray(approvers)) {
            continue;
        }

        for (const approver of approvers) {
            try {
                if (isUserDirectApprover(approver, userDescriptor)) {
                    return true;
                }

                if (await isUserGroupMemberApprover(approver, userDescriptor, context)) {
                    return true;
                }
            } catch (error) {
                console.error('Error checking approver:', approver.id, error);
            }
        }
    }

    return false;
};